import AlertMessageStore from "@/stores/AlertMessageStore";
import UserStore from "@/stores/UserStore";
import { AxiosInstance } from "axios";
import { SelectedPortfolioOption, toFollowUpCounts } from "@/portfolio/portfolioDomain";
import { failure, isOk, ok, pending, Result } from "@/common/common";
import { AccessType } from "@/types/enums/AccessType";
import { makeAutoObservable } from "mobx";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { formatDate } from "@/i18n/dateFormat";
import { Language } from "@/i18n/languages";
import { poaTypeLabels, PowerOfAttorney } from "@/types/CompanyInfo.ts";
import { TMessage } from "@/i18n/tt.ts";

export class FollowUpCompanyInfoStore {
  http: AxiosInstance;
  userStore: UserStore;
  messageStore: AlertMessageStore;
  companyInfoResponse?: Result<GetPortfolioCompanyInfoResponse>;
  searchField: StringFieldModel = new StringFieldModel();
  companySelect?: RadioFieldModel<string | null>;

  constructor(http: AxiosInstance, userStore: UserStore, messageStore: AlertMessageStore) {
    this.http = http;
    this.userStore = userStore;
    this.messageStore = messageStore;
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  *fetchCompanyInfo(portfolio: SelectedPortfolioOption, contextCompany?: string) {
    const url = toPortfolioCompanyInfoApiUrl(portfolio, contextCompany);
    if (!url) return;

    this.companyInfoResponse = pending();
    try {
      const response: GetPortfolioCompanyInfoResponse = yield this.http
        .get(url)
        .then((res) => res.data);
      this.companyInfoResponse = ok(response);

      this.initCompanySelect(response);
      this.userStore.dispatchEvent([
        { type: "update-store", data: { followUpCounts: toFollowUpCounts(response) } },
      ]);
    } catch (e) {
      this.companyInfoResponse = failure();
      this.messageStore.showGeneralError();
    }
  }

  filteredData(lang: Language): CompanyInfoFollowUp[] {
    if (!isOk(this.companyInfoResponse)) return [];
    const data = this.companyInfoResponse.data?.followUp || [];
    const filterData = this.companySelect?.value
      ? data.filter((d) => d.orgNo === this.companySelect?.value)
      : data;
    const search = this.searchField.inputValue.toLowerCase();
    return filterData.filter(
      (d) =>
        d.companyName.toLowerCase().includes(search) ||
        d.orgNo.toLowerCase().includes(search) ||
        (d.type === "aoaDateFollowUp" &&
          (formatDate(lang, d.brAoADate).toLowerCase().includes(search) ||
            formatDate(lang, d.lastApprovedDate).toLowerCase().includes(search))),
    );
  }

  initCompanySelect(response: GetPortfolioCompanyInfoResponse) {
    const companyMap = new Map(response.followUp.map((d) => [d.orgNo, d.companyName]));
    this.companySelect = new RadioFieldModel<string | null>({
      options: [null, ...Array.from(companyMap.keys())],
      value: null,
      idFn: (o) => (o ? o : "all"),
      labelFn: (o) => (o ? { translated: companyMap.get(o)! } : { key: "portfolio.all-companies" }),
    });
  }
}

type AoADateFollowUp = {
  type: "aoaDateFollowUp";
  orgNo: string;
  companyName: string;
  brAoADate: string;
  lastApprovedDate: string;
};

export type BrCheckFollowUp = {
  type: "brCheckFollowUp";
  orgNo: string;
  companyName: string;
  issueCount: number;
};

export type RenewalCandidateFollowUp = {
  type: "renewalCandidateFollowUp";
  orgNo: string;
  companyName: string;
  powerOfAttorneys: PowerOfAttorney[];
};

export type CompanyInfoFollowUp = AoADateFollowUp | BrCheckFollowUp | RenewalCandidateFollowUp;

export type GetPortfolioCompanyInfoResponse = {
  followUp: CompanyInfoFollowUp[];
  signDocumentCount: number;
  followUpMattersCount: number;
  companyInfoCount: number;
  followUpShareholdersCount: number;
};

const toPortfolioCompanyInfoApiUrl = (
  selectedPortfolioOption: SelectedPortfolioOption,
  contextCompany?: string,
): string | null => {
  const format = (part: string, context: string = "") =>
    "/portfolio" + part.toLowerCase() + "/company-info" + context;
  switch (selectedPortfolioOption.type) {
    case "all-companies":
      return format("", contextCompany ? `/${contextCompany}` : "");
    case "with-admin-role":
      return format(`/role/${AccessType.ADMIN}`);
    case "with-board-role":
      return null;
    case "my-investments":
      return null;
    case "portfolio-instance":
      return format(`/${selectedPortfolioOption.portfolio.id}`);
  }
};

export const descriptionRenewalCandidatePoA = (followUp: RenewalCandidateFollowUp): TMessage => {
  if (followUp.powerOfAttorneys.length !== 1) {
    return {
      key: "followup.company-info.poa-renewal-multiple",
      params: {
        renewalCount: followUp.powerOfAttorneys.length,
      },
    };
  }
  const poa = followUp.powerOfAttorneys[0];
  const poaType = { key: poaTypeLabels[poa.type] };
  if (poa.hasExpired) {
    return {
      key: "followup.company-info.poa-renewal-single-expired",
      params: {
        poaType,
        expiredDate: { type: "date", value: poa.expiryDate },
      },
    };
  }
  return poa.expiryDate
    ? {
        key: "followup.company-info.poa-renewal-single-nearing-expiry",
        params: {
          poaType,
          expiryDate: { type: "date", value: poa.expiryDate },
        },
      }
    : {
        key: "followup.company-info.poa-renewal-single-expired-soon",
        params: { poaType },
      };
};
